import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { postRegister } from "../../helpers/eirna_backend_helper";
import eirnaLogo from "../../assets/images/eirna/eirnabio-connect-logo.png";
import LoginCarousel from "./LoginCarousel";
import ReCaptcha from "../../Components/Common/ReCaptcha";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL parameters for pre-population
  const urlParams = new URLSearchParams(location.search);
  const emailFromUrl = urlParams.get("email") || "";
  const userTokenFromUrl = urlParams.get("user_token") || "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const recaptchaRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [passwordValidation, setPasswordValidation] = useState({
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasMinLength: false,
  });

  // Enhanced ReCaptcha handlers
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (token) {
      setRecaptchaError("");
    }
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
    setRecaptchaError("ReCaptcha expired. Please verify again.");
  };

  const handleRecaptchaError = (error) => {
    setRecaptchaToken(null);
    setRecaptchaError("ReCaptcha verification failed. Please try again.");
  };

  // Password validation function
  const validatePassword = (password) => {
    const validation = {
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasMinLength: password.length >= 12,
    };
    setPasswordValidation(validation);
    return validation;
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: emailFromUrl,
      password: "",
      usertoken: userTokenFromUrl,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email")
        .required("Please enter your email"),
      password: Yup.string()
        .required("Please enter your password")
        .test(
          "password-requirements",
          "Password does not meet requirements",
          function (value) {
            if (!value) return false;
            const validation = validatePassword(value);
            return (
              validation.hasLowercase &&
              validation.hasUppercase &&
              validation.hasNumber &&
              validation.hasMinLength
            );
          }
        ),
      usertoken: Yup.string().required("Please enter your user token"),
    }),
    onSubmit: async (values) => {
      // Validate ReCaptcha before submission
      if (!recaptchaToken) {
        setRecaptchaError("Please complete the ReCaptcha verification");
        return;
      }

      setLoading(true);
      setError("");
      setRecaptchaError("");

      try {
        const response = await postRegister({
          email: values.email,
          password: values.password,
          usertoken: values.usertoken,
          recaptcha: recaptchaToken,
        });

        if (response) {
          setSuccess(true);
        }
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message || err?.message || "Registration failed";
        setError(errorMessage);
        // Reset ReCaptcha on error
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setRecaptchaToken(null);
        }
      } finally {
        setLoading(false);
      }
    },
  });

  // Handle password change and validation
  const handlePasswordChange = (e) => {
    const password = e.target.value;
    validation.handleChange(e);
    validatePassword(password);
  };

  // Handle password field focus
  const handlePasswordFocus = () => {
    setPasswordFocused(true);
  };

  // Handle password field blur
  const handlePasswordBlur = (e) => {
    setPasswordFocused(false);
    validation.handleBlur(e);
  };

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (success) {
      setTimeout(() => navigate("/login"), 10000);
    }
  }, [success, navigate]);

  document.title = "Register | EIRNABio Connect";

  return (
    <React.Fragment>
      <div
        style={{
          width: "100vw",
          // minHeight: "100vh",
          margin: 0,
          padding: 0,
          overflow: "auto",
        }}
      >
        <Row
          className="g-0"

          // style={{ minHeight: "100vh", margin: 0 }}
        >
          {/* Left Panel - Carousel Section */}
          <Col
            xl={6}
            lg={6}
            md={6}
            className="d-none d-md-block"
            style={{
              padding: "0",
              position: "relative",
              // minHeight: "100vh",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: "100%",
                // height: "100vh",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoginCarousel />
            </div>
          </Col>

          {/* Right Panel - Register Form Section */}
          <Col
            xl={6}
            lg={6}
            md={6}
            xs={12}
            style={{
              backgroundColor: "white",
              minHeight: "100vh",
              display: "flex",
              alignItems: isMobile ? "center" : "flex-start",
              justifyContent: "center",
              padding: isMobile ? "10px 0" : "20px 0",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "500px",
                padding: isMobile ? "20px" : "40px",
                marginTop: isMobile ? "20px" : "0",
              }}
            >
              {/* EIRNABio Logo Section */}
              <div className="text-center mb-4">
                <Link to="/" className="d-inline-block auth-logo">
                  <img
                    src={eirnaLogo}
                    alt="EIRNABio Connect"
                    height={isMobile ? "60" : "80"}
                    style={{ marginBottom: isMobile ? "15px" : "20px" }}
                  />
                </Link>
              </div>

              {/* Welcome Text */}
              <div className="text-center mb-4">
                <h5
                  style={{
                    fontWeight: "600",
                    fontSize: isMobile ? "20px" : "24px",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  Create Your Account
                </h5>
                <p
                  style={{
                    color: "#666",
                    fontSize: isMobile ? "13px" : "14px",
                    margin: "0",
                  }}
                >
                  Join EIRNABio Connect to access advanced translatomics tools
                </p>
              </div>

              {/* Success Alert */}
              {success && (
                <Alert color="success" className="mb-4">
                  <i className="mdi mdi-check-circle me-2"></i>
                  An email has been sent to you from ebconnect@eirnabio.com.
                  Open the email and click on the confirmation link to complete
                  your account registration. If you do not receive this email in
                  the next few minutes, please check your spam folder.
                </Alert>
              )}

              {/* Error Alert */}
              {error && error ? (
                <Alert color="danger" className="mb-4">
                  <i className="mdi mdi-block-helper me-2"></i>
                  {error}
                </Alert>
              ) : null}

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                {/* Email Field */}
                <div className="mb-3">
                  <Label
                    htmlFor="email"
                    style={{
                      fontWeight: "500",
                      color: "#333",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Email
                  </Label>
                  <Input
                    name="email"
                    placeholder="Enter your email"
                    type="email"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ""}
                    invalid={
                      validation.touched.email && validation.errors.email
                        ? true
                        : false
                    }
                    style={{
                      padding: "12px 16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                  {validation.touched.email && validation.errors.email ? (
                    <FormFeedback type="invalid">
                      {validation.errors.email}
                    </FormFeedback>
                  ) : null}
                </div>

                {/* Password Field */}
                <div className="mb-3">
                  <Label
                    htmlFor="password"
                    style={{
                      fontWeight: "500",
                      color: "#333",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Password
                  </Label>
                  <div className="position-relative">
                    <Input
                      name="password"
                      value={validation.values.password || ""}
                      type={passwordShow ? "text" : "password"}
                      placeholder="Enter your password"
                      onChange={handlePasswordChange}
                      onFocus={handlePasswordFocus}
                      onBlur={handlePasswordBlur}
                      invalid={
                        validation.touched.password &&
                        validation.errors.password
                          ? true
                          : false
                      }
                      style={{
                        padding: "12px 16px",
                        paddingRight: "45px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        fontSize: "14px",
                      }}
                    />
                    <button
                      className="btn position-absolute"
                      type="button"
                      onClick={() => setPasswordShow(!passwordShow)}
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        border: "none",
                        background: "none",
                        color: "#666",
                        padding: "0",
                        width: "20px",
                        height: "20px",
                      }}
                    >
                      <i
                        className={
                          passwordShow ? "ri-eye-off-fill" : "ri-eye-fill"
                        }
                        style={{ fontSize: "16px" }}
                      ></i>
                    </button>
                  </div>

                  {/* Password Requirements */}
                  {passwordFocused && validation.values.password && (
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "12px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        fontSize: "12px",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: "4px",
                          fontWeight: "500",
                          color: "#666",
                        }}
                      >
                        Password Requirements:
                      </div>
                      <div
                        style={{
                          color: passwordValidation.hasLowercase
                            ? "#28a745"
                            : "#dc3545",
                          marginBottom: "2px",
                        }}
                      >
                        <i
                          className={
                            passwordValidation.hasLowercase
                              ? "ri-check-line"
                              : "ri-close-line"
                          }
                          style={{ marginRight: "4px" }}
                        ></i>
                        At least one lowercase letter
                      </div>
                      <div
                        style={{
                          color: passwordValidation.hasUppercase
                            ? "#28a745"
                            : "#dc3545",
                          marginBottom: "2px",
                        }}
                      >
                        <i
                          className={
                            passwordValidation.hasUppercase
                              ? "ri-check-line"
                              : "ri-close-line"
                          }
                          style={{ marginRight: "4px" }}
                        ></i>
                        At least one uppercase letter
                      </div>
                      <div
                        style={{
                          color: passwordValidation.hasNumber
                            ? "#28a745"
                            : "#dc3545",
                          marginBottom: "2px",
                        }}
                      >
                        <i
                          className={
                            passwordValidation.hasNumber
                              ? "ri-check-line"
                              : "ri-close-line"
                          }
                          style={{ marginRight: "4px" }}
                        ></i>
                        At least one number
                      </div>
                      <div
                        style={{
                          color: passwordValidation.hasMinLength
                            ? "#28a745"
                            : "#dc3545",
                        }}
                      >
                        <i
                          className={
                            passwordValidation.hasMinLength
                              ? "ri-check-line"
                              : "ri-close-line"
                          }
                          style={{ marginRight: "4px" }}
                        ></i>
                        At least 12 characters long
                      </div>
                    </div>
                  )}

                  {validation.touched.password && validation.errors.password ? (
                    <FormFeedback type="invalid" style={{ display: "block" }}>
                      {validation.errors.password}
                    </FormFeedback>
                  ) : null}
                </div>

                {/* User Token Field */}
                <div className="mb-3">
                  <Label
                    htmlFor="usertoken"
                    style={{
                      fontWeight: "500",
                      color: "#333",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    User Token
                  </Label>
                  <Input
                    name="usertoken"
                    placeholder="Enter your user token"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.usertoken || ""}
                    invalid={
                      validation.touched.usertoken &&
                      validation.errors.usertoken
                        ? true
                        : false
                    }
                    style={{
                      padding: "12px 16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                  {validation.touched.usertoken &&
                  validation.errors.usertoken ? (
                    <FormFeedback type="invalid">
                      {validation.errors.usertoken}
                    </FormFeedback>
                  ) : null}
                </div>

                {/* ReCaptcha */}
                <div className="mb-2 d-flex justify-content-center">
                  <ReCaptcha
                    ref={recaptchaRef}
                    siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                    onError={handleRecaptchaError}
                    theme="light"
                    size="normal"
                    hasError={!!recaptchaError}
                    errorMessage={recaptchaError}
                  />
                </div>

                {/* Submit Button */}
                <Button
                  color="success"
                  disabled={loading}
                  className="w-100"
                  type="submit"
                  style={{
                    backgroundColor: "#0A5F3E",
                    borderColor: "#0A5F3E",
                    padding: "12px",
                    fontSize: "16px",
                    fontWeight: "500",
                    borderRadius: "8px",
                    border: "none",
                    marginTop: "16px",
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2">
                        Loading...
                      </Spinner>
                      Creating Account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </Form>

              {/* Sign In Section */}
              <div
                className="text-center"
                style={{
                  marginTop: isMobile ? "20px" : "24px",
                  marginBottom: "20px",
                }}
              >
                <p
                  style={{
                    fontSize: isMobile ? "13px" : "14px",
                    color: "#666",
                    margin: "0",
                  }}
                >
                  Already have an account?{" "}
                  <Link to="/login"
                    style={{
                      color: "#0A5F3E",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    Sign In
                  </Link>
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default Register;